import React, { useEffect, useRef, useState } from "react";
import { X, CheckCircle2, AlertCircle, WifiOff, Phone, MessageCircle } from "lucide-react";
import { INTAKE, INTAKE_CONSENT } from "@/constants/testIds";
import { enqueueSubmission, flushQueue } from "@/lib/intakeQueue";
import { track } from "@/lib/analytics";

const SPONSOR_BUNDLES = [
  "Sponsor a Session (UK/Global) — £35",
  "4-Session Sponsorship Bundle (UK/Global) — £120",
  "10-Session Sponsorship Bundle (UK/Global) — £280",
];
const IMPACT_BUNDLES = [
  "Donate an Impact Session (UK/Global) — £35",
];

function bundlesFor(_audience, isImpact = false) {
  if (isImpact) return IMPACT_BUNDLES;
  return SPONSOR_BUNDLES;
}

export const IntakeModal = ({
  open,
  onClose,
  audience = "zim",
  preferredBundle = "",
  isOnline = true,
  onOpenPrivacy,
}) => {
  const isImpact = /impact session/i.test(preferredBundle);
  const [firstName, setFirstName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [prefWhatsapp, setPrefWhatsapp] = useState(true);
  const [prefCalls, setPrefCalls] = useState(false);
  const [relativeName, setRelativeName] = useState("");
  const [relativeContact, setRelativeContact] = useState("");
  const [bundle, setBundle] = useState(preferredBundle || bundlesFor(audience, /impact session/i.test(preferredBundle))[0]);
  const [suitability, setSuitability] = useState(false);
  const [keepInformed, setKeepInformed] = useState(false);
  const [consentRights, setConsentRights] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { kind: 'sent'|'queued'|'error', message }
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  // Reset when audience/bundle changes or modal opens
  useEffect(() => {
    if (open) {
      const impactNow = /impact session/i.test(preferredBundle);
      setBundle(preferredBundle || bundlesFor(audience, impactNow)[0]);
      setResult(null);
      setTimeout(() => firstFieldRef.current?.focus(), 50);
      track("intake_modal_opened", {
        audience,
        impact: impactNow,
        bundle: preferredBundle || null,
      });
    }
  }, [open, audience, preferredBundle]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!isImpact && !suitability) {
      setResult({
        kind: "error",
        message: "Please acknowledge the suitability call before submitting.",
      });
      return;
    }
    if (!consentRights) {
      setResult({
        kind: "error",
        message:
          "Please confirm you understand your data-protection rights before submitting.",
      });
      return;
    }
    setSubmitting(true);
    setResult(null);

    const prefs = [];
    if (prefWhatsapp) prefs.push("whatsapp");
    if (prefCalls) prefs.push("calls");

    const payload = {
      first_name: firstName.trim(),
      contact_number: isImpact ? null : contactNumber.trim(),
      email: email.trim() || null,
      contact_preferences: isImpact ? ["whatsapp"] : prefs,
      location: "uk_other",
      booking_for: "relative",
      relative_name: !isImpact ? (relativeName.trim() || null) : null,
      relative_contact_number: !isImpact ? (relativeContact.trim() || null) : null,
      preferred_bundle: bundle,
      suitability_ack: isImpact ? true : suitability,
      keep_me_informed: keepInformed,
      consent_data_use: false,
      consent_data_sharing: false,
      consent_rights_ack: consentRights,
      source_audience: audience,
    };

    // Always enqueue first (durable). Then try to flush.
    enqueueSubmission(payload);
    const r = await flushQueue();

    if (r.remaining === 0) {
      track("intake_submitted", {
        audience,
        impact: isImpact,
        bundle,
        offline: false,
      });
      setResult({
        kind: "sent",
        message:
          "Thank you. We've received your request and will call you back within one working day for the suitability conversation.",
      });
    } else {
      track("intake_queued_offline", {
        audience,
        impact: isImpact,
        bundle,
      });
      setResult({
        kind: "queued",
        message:
          "You're offline, so we've securely saved your request on this device. It will send automatically the moment your connection returns.",
      });
    }
    setSubmitting(false);
  };

  const handleReset = () => {
    setFirstName("");
    setContactNumber("");
    setEmail("");
    setRelativeName("");
    setRelativeContact("");
    setSuitability(false);
    setKeepInformed(false);
    setConsentRights(false);
    setResult(null);
  };

  const bundles = bundlesFor(audience, isImpact);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intake-modal-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close form"
        onClick={onClose}
        className="absolute inset-0 bg-[#1F2E4D]/40 backdrop-blur-sm"
      />

      {/* Panel */}
      <div
        ref={dialogRef}
        data-testid={INTAKE.modal}
        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-[#E8E2D4] max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#E8E2D4] flex items-center justify-between px-6 sm:px-8 py-4">
          <div>
            <h2
              id="intake-modal-title"
              className="font-serif text-xl text-[#1F2E4D] leading-tight"
            >
              {isImpact ? "Gift an Impact Session" : "Sponsor peace of mind"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={INTAKE.close}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-[#F0F5F5] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[#1F2E4D]" />
          </button>
        </div>

        {!isOnline && (
          <div
            data-testid={INTAKE.offlineBanner}
            className="mx-6 sm:mx-8 mt-5 rounded-2xl bg-[#FFF7E6] border border-[#F0D78B] text-[#7A5A00] px-4 py-3 flex items-start gap-3 text-sm"
            role="status"
          >
            <WifiOff className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
            <p className="leading-relaxed">
              You are currently offline. Your request will be securely saved on
              your device and will send automatically the moment your
              connection returns.
            </p>
          </div>
        )}

        {result ? (
          <div className="px-6 sm:px-8 py-8">
            {result.kind === "sent" && (
              <div data-testid={INTAKE.successMessage} className="flex flex-col items-start gap-4">
                <CheckCircle2 className="w-10 h-10 text-[#0E7C7B]" />
                <h3 className="font-serif text-2xl text-[#1F2E4D]">
                  We've got it.
                </h3>
                <p className="text-[#4A5568] leading-relaxed">
                  {result.message}
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 inline-flex items-center gap-2 bg-[#0E7C7B] hover:bg-[#0b6362] text-white rounded-full px-6 py-3 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            )}
            {result.kind === "queued" && (
              <div data-testid={INTAKE.successMessage} className="flex flex-col items-start gap-4">
                <WifiOff className="w-10 h-10 text-[#1F2E4D]" />
                <h3 className="font-serif text-2xl text-[#1F2E4D]">
                  Saved safely on your device.
                </h3>
                <p className="text-[#4A5568] leading-relaxed">
                  {result.message}
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 inline-flex items-center gap-2 bg-[#0E7C7B] hover:bg-[#0b6362] text-white rounded-full px-6 py-3 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            )}
            {result.kind === "error" && (
              <div data-testid={INTAKE.errorMessage} className="flex flex-col items-start gap-4">
                <AlertCircle className="w-10 h-10 text-[#C8412F]" />
                <h3 className="font-serif text-2xl text-[#1F2E4D]">
                  Something needs your attention.
                </h3>
                <p className="text-[#4A5568] leading-relaxed">
                  {result.message}
                </p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-2 inline-flex items-center gap-2 bg-[#0E7C7B] hover:bg-[#0b6362] text-white rounded-full px-6 py-3 font-medium transition-colors"
                >
                  Back to form
                </button>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 sm:px-8 py-6 space-y-7">
            {/* First name */}
            <div>
              <label className="text-xs tracking-[0.18em] uppercase font-semibold text-[#4A5568]">
                First name
              </label>
              <input
                ref={firstFieldRef}
                data-testid={INTAKE.firstName}
                type="text"
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-2 w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0E7C7B] py-2.5 text-lg text-[#1F2E4D] outline-none transition-colors"
                placeholder="Tafadzwa"
              />
            </div>

            {/* Contact number — UK +44 placeholder. Hidden in impact-donation flow. */}
            {!isImpact && (
              <div>
                <label className="text-xs tracking-[0.18em] uppercase font-semibold text-[#4A5568]">
                  Preferred contact number
                </label>
                <input
                  data-testid={INTAKE.contactNumber}
                  type="tel"
                  required={!isImpact}
                  inputMode="tel"
                  autoComplete="tel"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  className="mt-2 w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0E7C7B] py-2.5 text-lg text-[#1F2E4D] outline-none transition-colors"
                  placeholder="+44 7700 900 123"
                />
                <p className="text-xs text-[#4A5568] mt-3 mb-2">
                  How would you prefer we get in touch?
                </p>
                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-3 text-sm text-[#1F2E4D] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={INTAKE.contactPrefWa}
                      checked={prefWhatsapp}
                      onChange={(e) => setPrefWhatsapp(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-[#E2E8F0] text-[#0E7C7B] focus:ring-[#0E7C7B]"
                    />
                    <span className="inline-flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                      WhatsApp messages
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-[#1F2E4D] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={INTAKE.contactPrefRegular}
                      checked={prefCalls}
                      onChange={(e) => setPrefCalls(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-[#E2E8F0] text-[#0E7C7B] focus:ring-[#0E7C7B]"
                    />
                    <span className="inline-flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                      Phone calls
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Email address (always shown) + keep-me-informed underneath */}
            <div>
              <label
                htmlFor={INTAKE.email}
                className="text-xs tracking-[0.18em] uppercase font-semibold text-[#4A5568]"
              >
                {isImpact ? "Email address for updates" : "Email address"}
              </label>
              <input
                id={INTAKE.email}
                data-testid={INTAKE.email}
                type="email"
                required
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0E7C7B] py-2.5 text-lg text-[#1F2E4D] outline-none transition-colors"
                placeholder="you@example.com"
              />
              <label className="flex items-start gap-3 text-sm text-[#1F2E4D] mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={INTAKE.keepInformed}
                  checked={keepInformed}
                  onChange={(e) => setKeepInformed(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-[#E2E8F0] text-[#0E7C7B] focus:ring-[#0E7C7B]"
                />
                <span>Keep me informed of attendance.</span>
              </label>
            </div>

            {/* Relative's details — always shown (modal is sponsor-only). Hidden only in impact flow. */}
            {!isImpact && (
              <div className="rounded-2xl bg-[#E0EBEB]/40 border border-[#0E7C7B]/15 px-4 py-5 space-y-5">
                <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
                  Your relative's details
                </p>
                <div>
                  <label className="text-xs tracking-[0.18em] uppercase font-semibold text-[#4A5568]">
                    Relative's name
                  </label>
                  <input
                    data-testid={INTAKE.relativeName}
                    type="text"
                    required
                    value={relativeName}
                    onChange={(e) => setRelativeName(e.target.value)}
                    className="mt-2 w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0E7C7B] py-2.5 text-lg text-[#1F2E4D] outline-none transition-colors"
                    placeholder="Their first name"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.18em] uppercase font-semibold text-[#4A5568]">
                    Relative's contact number
                  </label>
                  <input
                    data-testid={INTAKE.relativeContact}
                    type="tel"
                    required
                    inputMode="tel"
                    value={relativeContact}
                    onChange={(e) => setRelativeContact(e.target.value)}
                    className="mt-2 w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0E7C7B] py-2.5 text-lg text-[#1F2E4D] outline-none transition-colors"
                    placeholder="+263 ..."
                  />
                  <p className="text-xs text-[#4A5568] mt-2 leading-relaxed">
                    We'll call them privately for the free suitability check
                    before any billing.
                  </p>
                </div>
              </div>
            )}

            {/* Suitability OR Impact-session explainer */}
            {isImpact ? (
              <p className="text-sm text-[#1F2E4D] leading-relaxed bg-[#E0EBEB]/60 rounded-2xl px-4 py-4">
                Your Impact Session will be delivered to an individual
                referred to us by charitable organisations throughout
                Zimbabwe. Every gift goes directly toward a 60-minute
                counselling call for someone who could not otherwise afford
                one.
              </p>
            ) : (
              <label className="flex items-start gap-3 text-sm text-[#1F2E4D] leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={INTAKE.suitability}
                  checked={suitability}
                  onChange={(e) => setSuitability(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-[#E2E8F0] text-[#0E7C7B] focus:ring-[#0E7C7B]"
                />
                <span>
                  I understand that once Heart Reach has spoken to my
                  relative, the team will contact me to confirm whether
                  telephone counselling is suitable for them.{" "}
                  <span className="font-semibold">
                    Billing for the sessions will only take place at that
                    point.
                  </span>
                </span>
              </label>
            )}

            {/* GDPR rights acknowledgement (UK GDPR + Zim CDPA 2021) */}
            <fieldset className="bg-[#F6F1E8] rounded-2xl px-4 py-4 border border-[#E8E2D4]">
              <legend className="px-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-[#0E7C7B]">
                Your data-protection rights
              </legend>
              <label className="flex items-start gap-3 text-sm text-[#1F2E4D] leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={INTAKE_CONSENT.rightsAck}
                  required
                  checked={consentRights}
                  onChange={(e) => setConsentRights(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-[#E2E8F0] text-[#0E7C7B] focus:ring-[#0E7C7B]"
                />
                <span>
                  I confirm that I have read and understood Heart Reach's{" "}
                  {onOpenPrivacy ? (
                    <button
                      type="button"
                      onClick={onOpenPrivacy}
                      className="underline underline-offset-2 text-[#0E7C7B] hover:text-[#0b6362]"
                    >
                      Privacy Policy
                    </button>
                  ) : (
                    <span className="underline underline-offset-2 text-[#0E7C7B]">Privacy Policy</span>
                  )}
                  . Under UK GDPR and Zimbabwe's Cyber and Data Protection Act
                  (2021) I understand my rights to access, rectify, restrict,
                  object to and erase my personal data, to withdraw consent at
                  any time without affecting the lawfulness of prior
                  processing, and to lodge a complaint with the UK
                  Information Commissioner's Office or Zimbabwe's POTRAZ.
                </span>
              </label>
            </fieldset>

            <button
              type="submit"
              data-testid={INTAKE.submit}
              disabled={submitting}
              className="w-full bg-[#0E7C7B] hover:bg-[#0b6362] text-white rounded-full px-6 py-4 font-medium transition-colors hr-pill-shadow disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : isImpact ? "Gift Now" : "Send my request"}
            </button>

            <p className="text-xs text-[#4A5568] leading-relaxed">
              Your data is managed securely in line with{" "}
              <span className="font-semibold text-[#1F2E4D]">
                Zimbabwe's Cyber and Data Protection Act (2021)
              </span>{" "}
              and{" "}
              <span className="font-semibold text-[#1F2E4D]">UK GDPR</span>.
              We will never share your private information without your
              consent.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default IntakeModal;
